import { useMultipleFeatureFlags } from '@agentnet/feature-flags';
import { UsersEmptyState } from '@fluentsms/agentnet-web-icons';
import { Link } from '@material-ui/core';
import { PartyOrderInsightsProps } from 'api/file/interfaces/get-file-order-insights';
import { PARTY_VERIFICATION_MESSAGE } from 'features/help/constants';
import VerificationEmptyState from 'features/services/shared/VerificationEmptyState';
import { Link as RouterLink } from 'react-router-dom';
import { FeatureFlag } from 'utilities/constants/launch-darkly-flags';
import PartyVerificationNonFaUwEmptyState from './PartyVerificationNonFaUwEmptyState';

interface PartyVerificationEmptyStateProps {
  isVerificationEnabled?: boolean;
  hasFraudValidationRights?: boolean;
  agentNetAdmin?: boolean;
  enableStatus: PartyOrderInsightsProps['enableStatus'];
  onAddNewVerification: () => void;
}

const PartyVerificationEmptyState = ({
  isVerificationEnabled,
  hasFraudValidationRights,
  agentNetAdmin,
  enableStatus,
  onAddNewVerification,
}: PartyVerificationEmptyStateProps) => {
  const [nonFaUw] = useMultipleFeatureFlags(FeatureFlag.NON_FA_UW_ANS_679_ENABLED);
  const isNonFaUwEnabled = nonFaUw.ready && !!nonFaUw.value;

  if (isNonFaUwEnabled) {
    return (
      <PartyVerificationNonFaUwEmptyState
        hasFraudValidationRights={hasFraudValidationRights}
        agentNetAdmin={agentNetAdmin}
        enableStatus={enableStatus}
        onAddNewVerification={onAddNewVerification}
      />
    );
  }

  const adminMessage = (
    <>
      Ready to start verifying parties? {agentNetAdmin ? 'Enable ' : 'Ask your admin to enable '}
      {agentNetAdmin ? (
        <>
          <Link
            component={RouterLink}
            to={`/admin/integrations-features?productCode=${enableStatus?.contactValidation?.productCode}`}
            underline="always"
            color="primary"
            data-testid="party-verification-link-contact-validation"
          >
            Contact Validation
          </Link>{' '}
          and{' '}
          <Link
            component={RouterLink}
            to={`/admin/integrations-features?productCode=${enableStatus?.enhancedIdentityVerification?.productCode}`}
            underline="always"
            color="primary"
            data-testid="party-verification-link-identity-verification"
          >
            Identity Verification
          </Link>{' '}
        </>
      ) : (
        'Contact Validation and Identity Verification '
      )}
      to get started.
    </>
  );

  return (
    <VerificationEmptyState
      icon={<UsersEmptyState />}
      headerText={isVerificationEnabled ? 'No Verifications' : 'Verify Parties with Confidence'}
      descriptionText={
        isVerificationEnabled
          ? 'Start verifying parties by clicking “New Verification” below.'
          : PARTY_VERIFICATION_MESSAGE
      }
      isProductEnabled={isVerificationEnabled}
      actionLabel="New Verification"
      onAction={onAddNewVerification}
      isActionDisabled={!hasFraudValidationRights}
      actionTestId="party-verification-new-verification-button-bottom"
      adminMessage={adminMessage}
    />
  );
};

export default PartyVerificationEmptyState;
